import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { Avatar, Group, Menu, Paper, Stack, Text } from "@mantine/core";
import { IUserDepricated } from "@mctypes/global.types";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { $getNodeByKey, LexicalNode, TextNode } from "lexical";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { $createMentionNode, MentionNode } from "../nodes/mentionNode";

const PUNCTUATION = "\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%'\"~=<>_:;";
const NAME = "\\b[A-Z][^\\s" + PUNCTUATION + "]";

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const CapitalizedNameMentionsRegex = new RegExp("(^|[^#])((?:" + DocumentMentionsRegex.NAME + "{" + 1 + ",})$)");

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ["@"].join("");

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = "[^" + TRIGGERS + PUNC + "\\s]";

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  "(?:" +
  "\\.[ |$]|" + // E.g. "r. " in "Mr. Smith"
  " |" + // E.g. " " in "Josh Duck"
  "[" +
  PUNC +
  "]|" + // E.g. "-' in "Salier-Hellendag"
  ")";

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
  "(^|\\s|\\()(" + "[" + TRIGGERS + "]" + "((?:" + VALID_CHARS + VALID_JOINS + "){0," + LENGTH_LIMIT + "})" + ")$",
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  "(^|\\s|\\()(" + "[" + TRIGGERS + "]" + "((?:" + VALID_CHARS + "){0," + ALIAS_LENGTH_LIMIT + "})" + ")$",
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5;

const mentionsCache = new Map();

function customLookupService(
  mentions: IProjectMemberApiItem[],
  searchString: string,
): Promise<IProjectMemberApiItem[]> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const filteredMentions = mentions?.filter((mention) =>
        (mention.user as IUserDepricated)?.name?.toLowerCase().includes(searchString.toLowerCase()),
      );
      resolve(filteredMentions);
    }, 500);
  });
}

const useMentionLookupService = (mentionData: IProjectMemberApiItem[]) => {
  const [results, setResults] = useState<IProjectMemberApiItem[]>([]);

  const searchMentions = useCallback(
    async (mentionString: string | null) => {
      if (mentionString === null) {
        setResults([]);
        return;
      }

      const cachedResults = mentionsCache.get(mentionString);
      if (cachedResults) {
        setResults(cachedResults);
        return;
      }

      const filteredMentions = await customLookupService(mentionData, mentionString);
      mentionsCache.set(mentionString, filteredMentions);
      setResults(filteredMentions);
    },
    [mentionData],
  );

  return { results, searchMentions };
};

function checkForCapitalizedNameMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  const match = CapitalizedNameMentionsRegex.exec(text);
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[2];
    if (matchingString != null && matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: matchingString,
      };
    }
  }
  return null;
}

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  const match = checkForAtSignMentions(text, 1);
  return match;
}

class MentionTypeaheadOption extends MenuOption {
  name: string;
  picture: JSX.Element;
  _id: string;

  constructor(name: string, picture: JSX.Element, id: string) {
    super(name);
    this.name = name;
    this.picture = picture;
    this._id = id;
  }
}

function MentionsTypeaheadMenuItem({
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: MentionTypeaheadOption;
}) {
  return (
    <Menu.Item
      key={option._id}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      style={{
        backgroundColor: isSelected ? "var(--mantine-color-blue-light)" : undefined,
      }}
    >
      <Group gap="sm">
        <Avatar size="sm" src={`/api/image/profilePictures/${option._id}`} name={option.name} />
        <Text size="sm">{option.name}</Text>
      </Group>
    </Menu.Item>
  );
}
const nodeIsMentionNode = (node: LexicalNode | null): node is MentionNode => {
  return node?.getType() === "mention";
};

export default function NewMentionsPlugin({
  handleMentions,
  members,
}: {
  handleMentions: (payload: string[]) => void;
  members: IProjectMemberApiItem[];
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [mentionNodeList, setMentionNodeList] = useState<{ nodeKey: string; mentionNode: MentionNode }[]>([]);
  const [queryString, setQueryString] = useState<string | null>(null);

  const { results, searchMentions } = useMentionLookupService(members);
  useEffect(() => {
    const metadataArray: string[] = mentionNodeList.map((node) => node.mentionNode.__metadata);
    handleMentions(metadataArray);
  }, [mentionNodeList]);

  useEffect(() => {
    searchMentions(queryString);
  }, [queryString, searchMentions]);

  useEffect(() => {
    if (!editor.hasNodes([MentionNode])) {
      throw new Error("MentionsPlugin: MentionNode not registered on editor");
    }

    return editor.registerMutationListener(MentionNode, (nodeMutations) => {
      //checks mentions in editor, and adds/removes them from mentionIdArray
      for (const [nodeKey, mutation] of nodeMutations) {
        if (mutation === "created") {
          editor.update(() => {
            const mentionNode = $getNodeByKey(nodeKey);
            if (nodeIsMentionNode(mentionNode)) {
              setMentionNodeList([...mentionNodeList, { nodeKey, mentionNode }]);
            } else {
              console.error("MentionsPlugin: MentionNode not found on created mutation");
            }
          });
        } else if (mutation === "destroyed") {
          const updatedNodeList = mentionNodeList.filter((node) => node.nodeKey !== nodeKey);
          setMentionNodeList(updatedNodeList);
        }
      }
    });
  }, [mentionNodeList, editor]);

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch("/", {
    minLength: 0,
  });

  const options: MentionTypeaheadOption[] = useMemo(
    () =>
      results
        .map((result) => ({
          ...result.user,
          name: result.user.name,
          _id: result.user._id,
          picture: null,
          key: null,
          setRefElement: null,
        }))
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results],
  );

  const onSelectOption = useCallback(
    (selectedOption: MentionTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption.name, selectedOption._id);
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
        }
        mentionNode.select();
        closeMenu();
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor);
      if (slashMatch !== null) {
        return null;
      }
      return getPossibleQueryMatch(text);
    },
    [checkForSlashTriggerMatch, editor],
  );

  return (
    <LexicalTypeaheadMenuPlugin
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(anchorElementRef, menuProps) =>
        options.length > 0 ? (
          <MentionsMenu anchorElementRef={anchorElementRef} options={options} {...menuProps} />
        ) : null
      }
    />
  );
}

function MentionsMenu({
  anchorElementRef,
  options,
  selectedIndex,
  setHighlightedIndex,
  selectOptionAndCleanUp,
}: {
  anchorElementRef: React.MutableRefObject<HTMLElement | null>;
  options: MentionTypeaheadOption[];
  selectedIndex: number | null;
  setHighlightedIndex: (index: number) => void;
  selectOptionAndCleanUp: (option: MentionTypeaheadOption) => void;
}) {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorElementRef.current as HTMLElement | null;
    // anchor.style.backgroundColor = "red";
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();

    if (rect.top === 0 && rect.left === 0) {
      return;
    }

    setCoords({
      top: rect.top + rect.height,
      left: rect.left,
    });
  }, [anchorElementRef, options.length, selectedIndex]);

  if (!coords) return null;

  return createPortal(
    <Paper
      withBorder
      shadow="md"
      radius="md"
      p={4}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        zIndex: 2000,
      }}
    >
      <Stack gap={2}>
        <Menu>
          {options.map((option, i: number) => (
            <MentionsTypeaheadMenuItem
              key={option._id}
              isSelected={selectedIndex === i}
              option={option}
              onMouseEnter={() => setHighlightedIndex(i)}
              onClick={() => {
                setHighlightedIndex(i);
                selectOptionAndCleanUp(option);
              }}
            />
          ))}
        </Menu>
      </Stack>
    </Paper>,
    document.body,
  );
}
